// ── moving in: reading the files a person drops, in their browser ───────────
//
// Any text file, a folder of them, or a zip of notes, read here so the person
// sees every file and what kind of thing it is before anything is sent. A
// chatgpt or claude export and an innernet archive are told apart and handed to
// their own lanes. Nothing leaves the browser until the person presses bring it in.
//
// Client-safe (jszip is loaded only when a zip is dropped).

import { kindOf, readable, splitSource, SOURCE_PART, TEXT_FILE, type IntakeSource, type SourceKind, type SourceTool } from './sources'
import { readArchiveZip } from '../export/readArchive'

export interface PickedFile extends IntakeSource {
  /** stable within one sheet: the path it came from */
  id: string
  size: number
}

export interface Picked {
  files: PickedFile[]
  /** names left out (not text, empty, or too large), said in one line */
  skipped: string[]
  /** a zip that is a chatgpt / claude export: its own lane */
  exportZip?: File
  /** a zip that is an innernet archive: its own lane */
  archiveZip?: File
}

/** a single file larger than this is not read (a notes export is far smaller; a 40 MB log is not memory) */
export const FILE_MAX_BYTES = 2_000_000
/** files in one pick at most */
export const PICK_MAX = 200

const pathOf = (f: File) => (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name

function picked(path: string, text: string): PickedFile | null {
  if (!readable(text)) return null
  const read = kindOf(path, text)
  return {
    id: path,
    title: path.split('/').slice(-3).join('/'),
    kind: read.kind,
    ...(read.kind === 'instructions' ? { for: read.for ?? '' } : {}),
    text,
    size: text.length,
  }
}

/** Read what was dropped or chosen. Never throws: what cannot be read is named in `skipped`. */
export async function readPicked(list: ReadonlyArray<File>): Promise<Picked> {
  const out: Picked = { files: [], skipped: [] }
  // tooling folders are never memory: left out before anything is counted, so a project folder's
  // node_modules never crowds out its notes
  const tooling = (f: File) => /(^|\/)(node_modules|\.git|\.next|dist|build)\//.test(pathOf(f).toLowerCase())
  const wanted = list.filter((f) => !tooling(f))
  // past the pick's size, said by name rather than dropped quietly
  for (const f of wanted.slice(PICK_MAX)) out.skipped.push(pathOf(f))
  for (const f of wanted.slice(0, PICK_MAX)) {
    const path = pathOf(f)
    const lower = path.toLowerCase()
    if (lower.endsWith('.zip')) {
      try {
        const bytes = await f.arrayBuffer()
        if (await readArchiveZip(bytes).catch(() => null)) {
          out.archiveZip = f
          continue
        }
        const JSZip = (await import('jszip')).default
        const zip = await JSZip.loadAsync(bytes)
        const names = Object.keys(zip.files)
        if (names.some((n) => /(^|\/)conversations\.json$/.test(n))) {
          out.exportZip = f
          continue
        }
        for (const name of names) {
          const entry = zip.files[name]
          if (entry.dir || !TEXT_FILE.test(name) || /(^|\/)__macosx\//i.test(name)) continue
          const text = await entry.async('string')
          if (text.length > FILE_MAX_BYTES) {
            out.skipped.push(name)
            continue
          }
          const p = picked(`${f.name}/${name}`, text)
          if (p) out.files.push(p)
          else out.skipped.push(name)
        }
      } catch {
        out.skipped.push(f.name)
      }
      continue
    }
    if (!TEXT_FILE.test(lower) || f.size > FILE_MAX_BYTES) {
      out.skipped.push(f.name)
      continue
    }
    try {
      const p = picked(path, await f.text())
      if (p) out.files.push(p)
      else out.skipped.push(f.name)
    } catch {
      out.skipped.push(f.name)
    }
  }
  return out
}

/** the kinds a person can say a file is, in the order a tap steps through them */
export const PICKABLE_KINDS: readonly SourceKind[] = ['instructions', 'memory', 'notes']

/** the next kind a tap on a file's kind gives */
export function nextKind(k: SourceKind): SourceKind {
  const i = PICKABLE_KINDS.indexOf(k)
  return PICKABLE_KINDS[(i + 1) % PICKABLE_KINDS.length]
}

/** the tools an instruction file can be for, as the sheet names them */
export const TOOL_WORDS: Record<SourceTool, string> = {
  '': 'every ai',
  claude: 'claude',
  chatgpt: 'chatgpt',
  cursor: 'cursor',
  gemini: 'gemini',
  copilot: 'copilot',
  codex: 'codex',
  windsurf: 'windsurf',
}

/** sources one send carries at most (the route refuses more than 60, never cutting them) */
export const SEND_SOURCES = 50

/** Split picked files into sends of at most `max` characters and SEND_SOURCES files; a file longer than that goes in its parts, never cut. Pure. */
export function sends(files: ReadonlyArray<IntakeSource>, max = 150_000, most = SEND_SOURCES): IntakeSource[][] {
  const parts = files.flatMap((f) => (f.text.length > max ? splitSource(f, SOURCE_PART) : [f]))
  const out: IntakeSource[][] = []
  let cur: IntakeSource[] = []
  let chars = 0
  for (const f of parts) {
    if (cur.length && (chars + f.text.length > max || cur.length >= most)) {
      out.push(cur)
      cur = []
      chars = 0
    }
    cur.push(f)
    chars += f.text.length
  }
  if (cur.length) out.push(cur)
  return out
}
